import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import {
  SR_API_ACTION_ADMIN_UPDATE_ITEM,
  SR_API_ACTION_ARCHIVE_RENTLINX_LISTING_LEAD,
  SR_API_ACTION_UNARCHIVE_RENTLINX_LISTING_LEAD,
} from 'api/actionTypes';

import {
  DATA_TABLE_SOURCE_PARTNER_STATS_RENTLINX_LEADS,
} from 'common/dataTableConstants';

import {
  LEAD_ARCHIVE_ALL_REASONS_OPTION_ITEMS,
} from 'common/leadConstants';

import {
  dataTable,
  actionDataTableLoadItems,
  actionDataTableRemoveItem,
  actionDataTableLoadNextPage,
} from 'client/helpers/dataTables';
import setNullEmptyValues from 'common/util/setNullEmptyValues';

import SrText from 'client/components/SrText';
import SrTable from 'client/components/SrTable';
import SrFrontConversation from 'client/components/SrFrontConversation';
import SrArchiveListingLeadModal from 'client/components/SrArchiveListingLeadModal';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';
import SrModalV2, {
  MODAL_THEME_STACK,
} from 'client/components/SrModalV2/SrModalV2';

import branch from 'client/core/branch';
import postWithState from 'client/actions/postWithState';
import withInfiniteScroll from 'client/helpers/withInfiniteScroll';
import SrFlex from './SrFlex';
import SrBox from './SrBox';

function getDataTableSettings({
  listingManagementCompany,
  listingId,
  apartmentId,
  sinceDate,
}) {
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_PARTNER_STATS_RENTLINX_LEADS,
    viewId: [
      'by',
      listingManagementCompany,
      listingId,
      apartmentId,
      sinceDate,
    ].filter((x) => x).join('-'),
    sort: {
      'rentlinxLeads.createdDate': 'desc',
    },
    count: 50,
    customParams: {
      partnerStatsRentlinxLeads: {
        listingManagementCompany,
        listingId,
        apartmentId,
        sinceDate,
      },
    },
  };

  return dataTableSettings;
}

const SrUnArchiveRentlinxLeadModal = ({
  onClose,
  onSubmit,
}) => {
  const unArchive = async () => {
    await onSubmit();
    onClose();
  };

  return (
    <SrModalV2
      showCloseButton={false}
      contentMaxWidth='500px'
      contentPadding='30px 40px 30px 30px'
      theme={MODAL_THEME_STACK}
    >
      <SrText
        font='greycliff'
        weight='800'
        size='26'
        lineHeight='33px'
        align='center'
        width='100%'
        display='block'
      >
        Un-Archive Lead
      </SrText>
      <SrBox margin='32px 0'>
        <SrText
          font='greycliff'
          fontSize='18px'
          weight='500'
          display='block'
          align='center'
        >
          Are you sure you want to Un-Archive this lead?
        </SrText>
      </SrBox>
      <SrFlex justify='between'>
        <SrBox width='48%'>
          <SrButton
            theme={SR_BUTTON_THEME_ADOBE_GREY}
            type='button'
            onClick={onClose}
          >
            Cancel
          </SrButton>
        </SrBox>
        <SrBox width='48%'>
          <SrButton
            theme={SR_BUTTON_THEME_ADOBE_GREEN}
            onClick={unArchive}
          >
            Un-Archive
          </SrButton>
        </SrBox>
      </SrFlex>
    </SrModalV2>
  );
};

@dataTable(getDataTableSettings)
@withInfiniteScroll({
  offset: 420,
})
@branch({
  frontConversationChannelId: ['env', 'FRONT_CHANNEL_LISTING_INQUIRIES_TEXTS'],
})
class SrDataTablePartnerStatRentlinxLeads extends Component {
  static propTypes = {
    items: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
    dataTableIsFullyLoaded: PropTypes.bool.isRequired,
    onInfiniteScrollWrapperRef: PropTypes.func.isRequired,
    onInfiniteScrollCallbackReady: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    frontConversationChannelId: PropTypes.string,
  }

  constructor(props) {
    super(props);

    const { onInfiniteScrollCallbackReady } = props;
    onInfiniteScrollCallbackReady(this._infiniteScrollCallback);

    this.state = {
      archiveRentlinxListingLeadId: null,
      unArchiveRentlinxListingLeadId: null,
    };
  }

  _infiniteScrollCallback = () => {
    const { dataTableIsFullyLoaded, dispatch } = this.props;
    if (!dataTableIsFullyLoaded) {
      dispatch(actionDataTableLoadNextPage, getDataTableSettings(this.props));
    }
  }

  _reloadDataTableRentlinxLead = (rentlinxLeadId) => {
    const { dispatch } = this.props;
    return dispatch(actionDataTableLoadItems, {
      dataSource: DATA_TABLE_SOURCE_PARTNER_STATS_RENTLINX_LEADS,
      ...getDataTableSettings(this.props),
      filter: {
        'rentlinxLeads.id': rentlinxLeadId,
      },
    });
  };

  _onHideLeadClick = (e) => {
    const { items, dispatch, onRefreshStats } = this.props;
    if (e.target.dataset.id) {
      const rentlinxListingLeadId = parseInt(e.target.dataset.id, 10);
      const { createdDate, rentlinxLead } = items.find((item) => item.id === rentlinxListingLeadId);

      if (!global.confirm(
        'Are you sure you want to hide the item ' +
        `"${[
          rentlinxLead.name,
          rentlinxLead.email,
          rentlinxLead.phone,
          momentWithAustinTimezone(createdDate).format('L'),
        ].join(', ')}"?`
      )) {
        return;
      }

      dispatch(postWithState, {
        action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
        payload: {
          table: 'rentlinxListingLeads',
          where: {
            id: rentlinxListingLeadId,
          },
          updates: {
            hiddenFromStats: true,
          },
        },
        onSuccess: () => {
          dispatch(actionDataTableRemoveItem, {
            dataSource: DATA_TABLE_SOURCE_PARTNER_STATS_RENTLINX_LEADS,
            entityId: rentlinxListingLeadId,
          });
          onRefreshStats();
        },
      });
    }
  }

  _formatRentlinxArchiveReason = ({ archiveReasons, partnerArchiveReason }) => {
    if (!archiveReasons.length) {
      return '';
    }

    const reasons = lodashCompact(
      archiveReasons
        .filter((reason) => reason)
        .map((reason) => (
          LEAD_ARCHIVE_ALL_REASONS_OPTION_ITEMS
            .find((item) => item.value === reason)?.label
        ))
    ).join(', ');

    return (
      <>
        {reasons}
        {partnerArchiveReason && (
          <>
            <br />
            <span style={{ maxWidth: 300, wordBreak: 'break-word' }}>
              &quot;{partnerArchiveReason}&quot;
            </span>
          </>
        )}
      </>
    );
  }

  _getRentlinxLeadArchiveValues = (archiveReasons) => {
    if (!archiveReasons.length) {
      return {
        text: 'Archive',
        color: 'blue300',
        cursor: 'pointer',
        onClick: (id) => this.setState({ archiveRentlinxListingLeadId: id }),
      };
    }

    return {
      text: 'Unarchive',
      color: 'blue300',
      cursor: 'pointer',
      onClick: (id) => this.setState({ unArchiveRentlinxListingLeadId: id }),
    };
  }

  _onSubmitArchiveListing = async (values) => {
    const { items, dispatch } = this.props;
    const { archiveRentlinxListingLeadId } = this.state;
    const { rentlinxLead } = items.find((item) => item.id === archiveRentlinxListingLeadId);

    await dispatch(postWithState, {
      action: SR_API_ACTION_ARCHIVE_RENTLINX_LISTING_LEAD,
      payload: setNullEmptyValues({
        rentlinxListingLeadId: archiveRentlinxListingLeadId,
        archiveReasons: values.archiveReasons,
        archiveReasonsDescription: values.archiveReasonsDescription,
        partnerArchiveReason: values.partnerArchiveReason,
      }),
    });

    this._reloadDataTableRentlinxLead(rentlinxLead.id);

    this.setState({ archiveRentlinxListingLeadId: null });
  };

  _onSubmitUnArchiveListing = async () => {
    const { items, dispatch } = this.props;
    const { unArchiveRentlinxListingLeadId } = this.state;
    const { rentlinxLead } = items.find((item) => item.id === unArchiveRentlinxListingLeadId);

    await dispatch(postWithState, {
      action: SR_API_ACTION_UNARCHIVE_RENTLINX_LISTING_LEAD,
      payload: setNullEmptyValues({
        rentlinxListingLeadId: unArchiveRentlinxListingLeadId,
      }),
    });

    this._reloadDataTableRentlinxLead(rentlinxLead.id);

    this.setState({ unArchiveRentlinxListingLeadId: null });
  };

  render() {
    const {
      items,
      dispatch,
      onInfiniteScrollWrapperRef,
      frontConversationChannelId,
    } = this.props;
    const {
      archiveRentlinxListingLeadId,
      unArchiveRentlinxListingLeadId,
    } = this.state;

    return (
      <div ref={onInfiniteScrollWrapperRef}>
        <SrTable
          headers={[
            { content: '#' },
            { content: 'archive reason' },
            { content: 'name' },
            { content: 'email' },
            { content: 'phone' },
            { content: 'created date' },
            { content: 'address' },
            { content: 'text' },
            { content: 'options' },
          ]}
          rows={items.map(({
            id,
            createdDate,
            rentlinxLead,
            listing,
            apartment,
            archiveReasons,
            partnerArchiveReason,
          }, i) => {
            const rentlinxTextValues = this._getRentlinxLeadArchiveValues(archiveReasons);

            return {
              cells: [
                {
                  content: `${i + 1}`,
                },
                {
                  content: this._formatRentlinxArchiveReason({ archiveReasons, partnerArchiveReason }),
                },
                {
                  content: rentlinxLead.name,
                },
                {
                  content: rentlinxLead.email,
                },
                {
                  content: rentlinxLead.phone,
                },
                {
                  content: momentWithAustinTimezone(createdDate).format('L h:mma'),
                },
                {
                  content: listing.addressStreetDisplay || apartment.name || '',
                },
                {
                  content: (
                    <>
                      {rentlinxLead.listingInquiriesConversationId && (
                        <SrFrontConversation
                          ctaClassName='tableCell'
                          frontConversation={{
                            conversationId: rentlinxLead.listingInquiriesConversationId,
                            channelId: frontConversationChannelId,
                          }}
                          user={rentlinxLead}
                          dispatch={dispatch}
                        />
                      )}
                    </>
                  ),
                },
                {
                  content: (
                    <>
                      <SrText
                        display='block'
                        color='blue300'
                        cursor='pointer'
                        onClick={this._onHideLeadClick}
                        data-id={id}
                      >
                        Hide
                      </SrText>
                      <SrText
                        display='block'
                        color={rentlinxTextValues.color}
                        cursor={rentlinxTextValues.cursor}
                        onClick={() => rentlinxTextValues.onClick(id)}
                        data-id={id}
                      >
                        {rentlinxTextValues.text}
                      </SrText>
                    </>
                  ),
                },
              ],
            };
          })}
        />
        {archiveRentlinxListingLeadId && (
          <SrArchiveListingLeadModal
            onClose={() => this.setState({ archiveRentlinxListingLeadId: null })}
            onSubmit={(values) => this._onSubmitArchiveListing(values)}
          />
        )}
        {unArchiveRentlinxListingLeadId && (
          <SrUnArchiveRentlinxLeadModal
            onClose={() => this.setState({ unArchiveRentlinxListingLeadId: null })}
            onSubmit={this._onSubmitUnArchiveListing}
          />
        )}
      </div>
    );
  }
}

SrDataTablePartnerStatRentlinxLeads.displayName = 'SrDataTablePartnerStatRentlinxLeads';

export default SrDataTablePartnerStatRentlinxLeads;
